import React from 'react';
import { DollarSign, Zap, Timer, Percent, Info, CreditCard, Building2, MessageCircle } from 'lucide-react';
import { UserProfile, FinancialConfig } from '../../types';
import { formatCurrencyBRL, formatCurrencyMask, parseCurrencyMask } from '../../utils/formatUtils';
import { HideOnPlayStore } from '../HideOnPlayStore';

interface FinancialStepProps {
  formData: UserProfile;
  financialData: FinancialConfig;
  derivedHourlyRate: number;
  onFinancialChange: (field: keyof FinancialConfig, value: any) => void;
  onTriggerAutoSave: () => void;
  onOpenPaywall: (plan?: 'monthly' | 'annual' | 'specialist' | 'specialist_annual') => void;
  onOpenCancelSurvey: () => void;
  formatDateLong: (dateStr: string) => string;
  onNavigateToCostCenter?: () => void;
}

export default function FinancialStep({
  formData,
  financialData,
  derivedHourlyRate,
  onFinancialChange,
  onTriggerAutoSave,
  onOpenPaywall,
  onOpenCancelSurvey,
  formatDateLong,
  onNavigateToCostCenter
}: FinancialStepProps) {
  return (
    <div className="space-y-4 overflow-x-hidden w-full max-w-full box-border">
      {/* Indicador do Valor-Hora calculado — feedback visual em tempo real */}
      <div className="bg-slate-900 text-white rounded-2xl p-5 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-brand/20 rounded-full blur-3xl -mr-16 -mt-16" />
        <div className="relative z-10">
          <p className="text-[9px] uppercase font-bold tracking-[0.2em] text-slate-400 mb-1">Seu Valor-Hora Calculado</p>
          <p className="text-3xl font-black">
            {formatCurrencyBRL(derivedHourlyRate)}
            <span className="text-sm font-medium text-slate-400 ml-1">/hora</span>
          </p>
          <p className="text-[10px] text-slate-400 mt-2">(Seu Salário Desejado + Gastos Fixos) ÷ Horas Trabalhadas no Mês</p>
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Seu Salário Mensal Desejado (Pró-labore)</label>
        <div className="relative">
          <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <input
            type="text"
            inputMode="decimal"
            value={formatCurrencyMask(((financialData.proLaboreGoal || 0) * 100).toString())}
            onChange={(e) => onFinancialChange('proLaboreGoal', parseCurrencyMask(e.target.value))}
            onBlur={onTriggerAutoSave}
            className="w-full bg-white border border-zinc-100 rounded-2xl py-4 pl-12 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all font-mono font-bold"
          />
        </div>
      </div>

      <div className="space-y-1 box-border">
        <div className="flex items-center justify-between px-1">
          <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Custo Fixo Mensal</label>
          <span className="text-[9px] text-blue-600 font-bold">Base Contábil</span>
        </div>
        <div className="relative">
          <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <input
            type="text"
            inputMode="decimal"
            value={formatCurrencyMask(((financialData.monthlyFixedCost || 0) * 100).toString())}
            onChange={(e) => onFinancialChange('monthlyFixedCost', parseCurrencyMask(e.target.value))}
            onBlur={onTriggerAutoSave}
            className="w-full bg-white border border-zinc-100 rounded-2xl py-4 pl-12 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all font-mono font-bold"
          />
          {onNavigateToCostCenter && (
            <button 
              type="button"
              onClick={onNavigateToCostCenter}
              className="mt-2 sm:mt-0 sm:absolute sm:right-3 sm:top-1/2 sm:-translate-y-1/2 bg-amber-100 text-amber-700 px-3 py-2 rounded-xl text-[9px] font-black uppercase tracking-tighter hover:bg-amber-200 transition-all w-full sm:w-auto text-center cursor-pointer"
            >
              Ajustar no Inventário
            </button>
          )}
        </div>
        <p className="text-[9px] text-zinc-400 mt-1 ml-1 leading-tight italic">
          *Soma automática de despesas fixas e desgaste de máquinas.
        </p>
      </div>

      <div className="space-y-1">
        <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Horas de Trabalho por Semana</label>
        <div className="relative">
          <Timer className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <input
            type="number"
            inputMode="numeric"
            min="1"
            max="168"
            value={financialData.weeklyWorkHours || ''}
            onChange={(e) => onFinancialChange('weeklyWorkHours', Math.min(168, Math.max(0, Number(e.target.value))))}
            onBlur={onTriggerAutoSave}
            className="w-full bg-white border border-zinc-100 rounded-2xl py-4 pl-12 pr-16 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all font-mono font-bold"
            placeholder="44"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-zinc-400 font-bold">horas</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Taxa Maquininha (%)</label>
          <div className="relative">
            <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <input
              type="number"
              inputMode="decimal"
              step="0.1"
              value={financialData.paymentFeePercentage || ''}
              onChange={(e) => onFinancialChange('paymentFeePercentage', Number(e.target.value))}
              onBlur={onTriggerAutoSave}
              className="w-full bg-white border border-zinc-100 rounded-2xl py-4 pl-12 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all font-mono font-bold box-border"
              placeholder="0.0"
            />
          </div>
        </div>

        <div className="space-y-1">
          <div className="flex items-center gap-1.5 ml-1 mb-1">
            <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Comissão Padrão (%)</label>
            <div className="relative group flex items-center">
              <Info className="w-3.5 h-3.5 text-zinc-400 cursor-help" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-zinc-900 text-white text-[10px] p-2.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-center shadow-xl leading-relaxed">
                A comissão é calculada sobre o preço final de venda. O sistema já faz o cálculo automático para garantir que esse custo não saia do seu lucro.
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-zinc-900"></div>
              </div>
            </div>
          </div>
          <div className="relative">
            <Percent className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <input
              type="number"
              inputMode="decimal"
              step="0.1"
              value={financialData.defaultCommissionPercentage || ''}
              onChange={(e) => onFinancialChange('defaultCommissionPercentage', Number(e.target.value))}
              onBlur={onTriggerAutoSave}
              className="w-full bg-white border border-zinc-100 rounded-2xl py-4 pl-12 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all font-mono font-bold box-border"
              placeholder="0.0"
            />
          </div>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-100 p-3 rounded-xl flex gap-2">
        <Info className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-[10px] text-amber-800 leading-tight">
          Ao salvar, o <strong>Valor da Hora</strong> será recalculado automaticamente e aplicado em <strong>todos os orçamentos futuros</strong>. Os orçamentos já gerados não são afetados.
        </p>
      </div>

      {/* Seção Minha Assinatura */}
      <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-4 space-y-3 mt-4 box-border w-full max-w-full">
        <div className="flex items-center gap-2 mb-1">
          <CreditCard className="h-4 w-4 text-[#004B87]" />
          <p className="text-xs font-bold text-zinc-900 uppercase tracking-widest">Minha Assinatura R-MAX</p>
        </div>
        
        <div className="bg-white border border-zinc-100 rounded-xl p-3 flex items-center justify-between box-border w-full max-w-full">
          <div>
            <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider">Plano Atual</p>
            <p className="text-sm font-black text-zinc-900">
              {formData.plan === 'Empresa' || formData.plan === 'Enterprise' || formData.plan === 'Empresarial'
                ? 'R-MAX Plano Empresa 💎'
                : formData.plan === 'Especialista'
                  ? 'R-MAX Especialista 👑'
                  : formData.plan === 'Pro'
                    ? 'R-MAX Pro 🚀'
                    : 'R-MAX Grátis 🎯'}
            </p>
          </div>
          <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-lg ${
            formData.subscriptionStatus === 'past_due'
              ? 'bg-amber-100 text-amber-800 border border-amber-300'
              : formData.plan === 'Empresa' || formData.plan === 'Enterprise' || formData.plan === 'Empresarial'
                ? 'bg-blue-100 text-blue-800 border border-blue-200'
                : formData.plan === 'Especialista'
                  ? 'bg-orange-100 text-orange-800 border border-orange-200'
                  : formData.plan === 'Pro' 
                    ? (formData.trialUsed === false ? 'bg-amber-100 text-amber-700 border border-amber-200' : 'bg-emerald-100 text-emerald-700 border border-emerald-200')
                    : 'bg-zinc-100 text-zinc-500 border border-zinc-200'
          }`}>
            {formData.subscriptionStatus === 'past_due'
              ? 'Tolerância Ativa (Carência)'
              : formData.plan === 'Empresa' || formData.plan === 'Enterprise' || formData.plan === 'Empresarial'
                ? 'Plano Empresa Ativo'
                : formData.plan === 'Especialista'
                  ? 'Especialista Ativo'
                  : formData.plan === 'Pro'
                    ? (formData.trialUsed === false ? 'Trial Ativo' : 'Ativo')
                    : 'Limitação Ativa'}
          </span>
        </div>

        {(formData.plan === 'Pro' || formData.plan === 'Especialista' || formData.plan === 'Empresa' || formData.plan === 'Enterprise' || formData.plan === 'Empresarial') && formData.accessEndsAt && (
          <div className="text-[10px] text-zinc-500 flex justify-between px-1">
            <span>
              {formData.subscriptionStatus === 'past_due' 
                ? 'Carência de acesso até:' 
                : formData.trialUsed === false 
                  ? 'Período de testes válido até:' 
                  : 'Acesso válido até:'}
            </span>
            <span className="font-bold text-zinc-700">{formatDateLong(formData.accessEndsAt.split('T')[0])}</span>
          </div>
        )}

        {formData.subscriptionStatus === 'past_due' && (
          <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 space-y-1">
            <div className="flex items-center gap-2 text-amber-800">
              <Timer className="h-4 w-4 text-amber-600 animate-pulse" />
              <span className="text-[10px] font-black uppercase tracking-wider">Período de Carência Ativo</span>
            </div>
            <p className="text-[10px] text-amber-700 leading-normal">
              A renovação do seu plano falhou no cartão. Seu acesso segue liberado por até 7 dias enquanto novas tentativas automáticas são feitas.
            </p>
          </div>
        )}

        {formData.subscriptionStatus === 'pending_payment' && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-1">
            <div className="flex items-center gap-2 text-amber-800">
              <Timer className="h-4 w-4 animate-pulse text-amber-600" />
              <span className="text-[10px] font-black uppercase tracking-wider">Pagamento Pendente</span>
            </div>
            <p className="text-[10px] text-amber-700 leading-normal">
              Aguardando confirmação do pagamento via PIX ou Boleto pelo Mercado Pago. O acesso será liberado automaticamente em instantes.
            </p>
          </div>
        )}

        {(formData.plan === 'Empresa' || formData.plan === 'Enterprise' || formData.plan === 'Empresarial') && formData.trialUsed !== false ? (
          <div className="text-center text-[10px] text-blue-800 py-3 bg-blue-50 rounded-xl border border-blue-200/60 font-bold uppercase tracking-wider cursor-default">
            💎 Plano Empresa Ativo
          </div>
        ) : formData.plan === 'Especialista' && formData.trialUsed !== false ? (
          <div className="text-center text-[10px] text-amber-800 py-3 bg-amber-50 rounded-xl border border-amber-200/60 font-bold uppercase tracking-wider cursor-default">
            👑 Plano Especialista Premium Ativo
          </div>
        ) : (
          <div className="space-y-2">
            {formData.plan === 'Pro' && formData.trialUsed === false && (
              <div className="text-center text-[10px] text-zinc-500 py-3 bg-zinc-100 rounded-xl border border-zinc-200/60 font-bold uppercase tracking-wider cursor-default mb-2">
                Período de testes ativo via Cupom de Indicação
              </div>
            )}
            <HideOnPlayStore fallback={
              <p className="text-center text-[10px] text-zinc-600 leading-normal bg-zinc-100 rounded-xl p-3 border border-zinc-200/60 font-medium">
                As assinaturas são feitas pelo nosso site. Abra o navegador de internet do seu celular ou computador para escolher seu plano.
              </p>
            }>
              {formData.subscriptionStatus === 'pending_payment' && formData.mpPreferenceId && (
                <a
                  href={
                    formData.mpPreferenceId.startsWith("preapproval-")
                      ? `https://www.mercadopago.com.br/preapproval/client/signin?preapproval_id=${formData.mpPreferenceId}`
                      : `https://www.mercadopago.com.br/checkout/v1/redirect?pref_id=${formData.mpPreferenceId}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3.5 bg-amber-500 hover:bg-amber-600 text-black font-black rounded-xl text-xs uppercase tracking-widest transition-all shadow-md shadow-amber-500/10 cursor-pointer flex items-center justify-center gap-2 text-center"
                >
                  Concluir Pagamento no Mercado Pago
                </a>
              )}
              <button
                type="button"
                onClick={() => onOpenPaywall(formData.plan === 'Pro' ? 'specialist' : 'annual')}
                className={`w-full py-3.5 ${
                  formData.plan === 'Pro'
                    ? 'bg-linear-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500'
                    : 'bg-linear-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
                } text-white font-black rounded-xl text-xs uppercase tracking-widest transition-all shadow-md cursor-pointer flex items-center justify-center gap-2`}
              >
                {formData.subscriptionStatus === 'pending_payment' 
                  ? 'Gerar Novo Link de Pagamento' 
                  : (formData.plan === 'Pro' ? 'Migrar para Especialista Premium' : 'Ver Planos e Assinar')}
                <Zap className="h-3.5 w-3.5" />
              </button>
            </HideOnPlayStore>
          </div>
        )}

        {/* Canal Direto de Feedback e Sugestões do Avaliador / Beta Tester */}
        <div className="bg-emerald-50/90 border border-emerald-200 rounded-xl p-3.5 space-y-2 mt-2">
          <div className="flex items-center gap-2 text-emerald-800">
            <MessageCircle className="h-4 w-4 text-emerald-600" />
            <span className="text-[10px] font-black uppercase tracking-wider">Canal de Feedback & Sugestões</span>
          </div>
          <p className="text-[11px] text-emerald-950 leading-relaxed font-medium">
            Encontrou alguma inconsistência, erro técnico ou tem uma sugestão de melhoria? Fale diretamente com o desenvolvedor responsável.
          </p>
          <a
            href={`https://wa.me/5511920897453?text=${encodeURIComponent(
              `Olá! Estou utilizando o R-MAX no plano ${formData.plan || 'Especialista'} e gostaria de relatar um feedback / sugestão:`
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 active:scale-[0.99] text-white font-black rounded-lg text-xs uppercase tracking-wider transition-all shadow-xs cursor-pointer text-center"
          >
            <MessageCircle className="h-4 w-4" />
            Relatar Erro ou Sugestão no WhatsApp
          </a>
        </div>
      </div>

      {/* Botão de Cancelamento de Assinatura (Regra do Ponto 2) */}
      {(formData.plan === 'Pro' || formData.plan === 'Especialista' || formData.plan === 'Empresa' || formData.plan === 'Enterprise' || formData.plan === 'Empresarial') && formData.trialUsed !== false && (
        <div className="text-center pt-2">
          <button
            type="button"
            onClick={onOpenCancelSurvey}
            className="text-xs text-zinc-400 hover:text-rose-500 transition-colors focus:outline-none font-medium cursor-pointer"
          >
            Cancelar Assinatura
          </button>
        </div>
      )}
    </div>
  );
}
